<script setup lang="ts">
  import {
    computed,
    nextTick,
    onActivated,
    onBeforeUnmount,
    onMounted,
    reactive,
    ref,
    watch,
  } from 'vue';
  import {
    actions as actionTypes,
    useUserComponent,
    userComponentProps,
    useHelpers,
    createComponentScope,
    Resource,
    uniqueID,
  } from '@integro/core';
  import type { IAppHelpers } from '@integro/core';
  import * as _ from 'lodash';

  defineOptions({
    name: 'UcContractClientCard',
  });

  const componentMetadata = {
    kind: 'usercontrol',
  };

  const emit = defineEmits(['reload', 'close', 'save']);

  const props = defineProps({
    ...userComponentProps,
    item: {
      type: Object,
      required: true,
    },
  });

  const {
    $app,
    $fn,
    $i18n,
    $store,
    $config,
    $imodal,
    $notification,
    $tabs,
    $route,
    $router,
    gettext,
    pgettext,
    ngettext,
    npgettext,
    schemaDefaults,
    getSchema,
    searchDataSource,
    randomElementId,
    datetime,
    date,
    time,
    number,
    money,
    distance,
    username,
    filesize,
  } = useHelpers();

  const ux = ref([]);

  const $data = computed(() => ({
    ux: ux.value,
  }));

  const {
    loading,
    error,
    loaded,
    reloading,
    showLoadingIndicator,
    loadDataThrottled,
    friendlyName,
    loadData,
    touchLastLoaded,
    handleError,
    setData,
    forms,
    registerForm,
    unregisterForm,
    canLoadData,
    isModalComponent,
    modalName,
    pageDocument: _pageDocument,
    routeTab,
    ux: _ux,
    componentMetadata: _componentMetadata,
    $uid,
    lastLoaded,
  } = useUserComponent(props, {
    metadata: componentMetadata,
    dataSpecs: [{ name: 'ux' }],
    loaders: {},
    ux: {},
    dataRefs: { ux },
  });

  const $component = createComponentScope(
    {
      $app,
      $fn,
      $i18n,
      $store,
      $imodal,
      $notification,
      $tabs,
      $router,
    } as IAppHelpers,
    {
      emit,
      forms,
      error,
      isModalComponent,
      modalName,
      componentMetadata,
      loadData,
      registerForm,
      unregisterForm,
      setData,
      touchLastLoaded,
      handleError,
    }
  );

  watch(
    () => props.item,
    (newValue, oldValue) => {
      if (_.isEqual(newValue, oldValue)) return;
      loadDataThrottled('item');
    }
  );

  const act_7315092382398562_reloadComponentData_biw4ozs1a =
    new actionTypes.ReloadComponentDataAction({});
  const inst_evh_7315092382398562_reload_0vatfgca1 =
    new actionTypes.EventHandlerImpl({
      action: act_7315092382398562_reloadComponentData_biw4ozs1a,
      event: 'reload',
      displayName: 'reloadComponentData',
    });
  const inst_evh_7315092382398562_reload = new actionTypes.EventHandlerGroup({
    handlers: [inst_evh_7315092382398562_reload_0vatfgca1],
    event: 'reload',
  });

  const actions = {
    inst_evh_7315092382398562_reload_0vatfgca1,
    inst_evh_7315092382398562_reload,
  };

  const evh_7315092382398562_reload_0vatfgca1 = async (event, scope) => {
    return await actions.inst_evh_7315092382398562_reload_0vatfgca1.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_7315092382398562_reload = async (event, scope) => {
    return await actions.inst_evh_7315092382398562_reload.executeFromDOM(
      $component,
      event,
      scope
    );
  };

  defineExpose({
    loadData,
    loading,
    loaded,
    error,
  });
</script>

<template>
  <ItpUsercontrol
    :loading="loading"
    :reloading="reloading"
    :loaded="loaded"
    :error="error"
    @reload="evh_7315092382398562_reload($event, {})"
  >
    <div v-if="item.head_office">
      <ItpText muted>{{ pgettext('Client', 'Head Office') }}:</ItpText>
      {{ item.head_office }}
    </div>
    <div v-if="item.branch_office">
      <ItpText muted>{{ pgettext('Client', 'Branch Office') }}:</ItpText>
      {{ item.branch_office }}
    </div>
    <div v-if="item.statistical_code">
      <ItpText muted>{{ pgettext('Client', 'Statistical Code') }}:</ItpText>
      {{ item.statistical_code }}
    </div>
    <div v-if="item.registration_number">
      <ItpText muted>{{ pgettext('Client', 'Registration Number') }}:</ItpText>
      {{ item.registration_number }}
    </div>
    <div v-if="item.account_number">
      <ItpText muted>{{ pgettext('Client', 'Bank Account') }}:</ItpText>
      {{ item.account_number }}
    </div>
    <div v-if="item.representative">
      <ItpText muted>{{ pgettext('Contract', 'Representatives') }}:</ItpText>
      {{ item.representative }}
    </div>
  </ItpUsercontrol>
</template>
